import router from "@/app/router";
import config from "@/app/configs";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import { buildKioskApiUrl } from "@/kiosk/utils/kioskApiUrl.utils";
import {
  clearKioskSession,
  getKioskIdToken,
  getKioskSessionTokenHash,
  isKioskSessionRevoked,
} from "@/kiosk/services/kioskAuth.service";
import type { KioskContextResponse, KioskPublicBrandingResponse } from "@/kiosk/types/kiosk";

const FORCED_LOGOUT_CODES = new Set(["kauth_03", "kauth_04", "kauth_05", "kauth_06"]);
const kioskApiBase = config?.apis.base || "";
let kioskSessionInvalidationPromise: Promise<void> | null = null;

export class KioskRequestError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.name = "KioskRequestError";
    this.status = status;
    this.data = data;
  }
}

const buildUrl = (path: string): string => buildKioskApiUrl(kioskApiBase, path);

const parseResponseBody = async (response: Response) => {
  const rawBody = await response.text();
  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return rawBody;
  }
};

const getKioskResponseErrorMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const directMessage = record.message || record.error;
    if (typeof directMessage === "string" && directMessage.trim()) {
      return directMessage;
    }
  }

  return getErrorMessage(payload, fallback);
};

const getKioskAuthErrorCode = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidateValues = [
    (payload as Record<string, unknown>).code,
    (payload as Record<string, unknown>).errorCode,
  ]
    .filter((value): value is string => typeof value === "string")
    .map((value) => value.toLowerCase());

  return candidateValues.find((value) => FORCED_LOGOUT_CODES.has(value)) ?? null;
};

const redirectToKioskLogin = async (): Promise<void> => {
  const tokenHash = router.currentRoute.value.params?.tokenHash;
  if (typeof tokenHash !== "string" || !tokenHash) {
    return;
  }

  await router.push({
    name: "kioskPinLogin",
    params: { tokenHash },
    query: { reason: "expired" },
  });
};

const invalidateKioskSession = async (): Promise<void> => {
  if (!kioskSessionInvalidationPromise || !isKioskSessionRevoked()) {
    kioskSessionInvalidationPromise = (async () => {
      await clearKioskSession({ revoked: true });
      await redirectToKioskLogin();
    })();
  }

  await kioskSessionInvalidationPromise;
};

export const validateKioskSession = async (tokenHash?: string): Promise<KioskContextResponse> => {
  const currentTokenHash = getKioskSessionTokenHash();

  if (tokenHash && currentTokenHash && currentTokenHash !== tokenHash) {
    await invalidateKioskSession();
    throw new Error("Kiosk session expired");
  }

  return kioskFetchJson<KioskContextResponse>("kiosk/context");
};

export const kioskFetch = async (path: string, init?: RequestInit): Promise<Response> => {
  if (isKioskSessionRevoked()) {
    throw new Error("Kiosk session expired");
  }

  const idToken = await getKioskIdToken();
  const headers = new Headers(init?.headers || {});

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Authorization", `Bearer ${idToken}`);

  const response = await fetch(buildUrl(path), {
    ...init,
    headers,
  });

  if (response.status === 401) {
    const data = await parseResponseBody(response.clone());
    const kioskAuthErrorCode = getKioskAuthErrorCode(data);

    if (kioskAuthErrorCode) {
      await invalidateKioskSession();
      throw new Error(getErrorMessage(data, "Kiosk session expired"));
    }
  }

  if (isKioskSessionRevoked()) {
    await invalidateKioskSession();
    throw new Error("Kiosk session expired");
  }

  return response;
};

export const kioskFetchJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await kioskFetch(path, init);
  const data = await parseResponseBody(response);

  if (!response.ok) {
    throw new KioskRequestError(
      response.status,
      getKioskResponseErrorMessage(data, "Kiosk request failed"),
      data,
    );
  }

  return data as T;
};

export const kioskPublicFetchJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(buildUrl(path), init);
  const data = await parseResponseBody(response);

  if (!response.ok) {
    throw new KioskRequestError(
      response.status,
      getKioskResponseErrorMessage(data, "Kiosk request failed"),
      data,
    );
  }

  return data as T;
};

export const fetchKioskBranding = async (
  tokenHash: string,
): Promise<KioskPublicBrandingResponse> => {
  return kioskPublicFetchJson<KioskPublicBrandingResponse>(
    `kioskPublic/${encodeURIComponent(tokenHash)}/branding`,
  );
};
